import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { blogPosts } from './blog-posts';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function BlogPost() {
  const { slug } = useParams();
  const { openAuthModal } = useAuth();
  const post = blogPosts.find(p => p.slug === slug || (p.aliases && p.aliases.includes(slug)));

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const postUrl = `https://squarecount.io/blog/${post.slug}`;
  const imageUrl = post.image || 'https://images.unsplash.com/photo-1585791728063-fd31db7ffd4a?w=1200&q=80';

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>{post.title} | SquareCount Blog</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={postUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={postUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={imageUrl} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        {post.tags.map((tag, i) => (
          <meta key={i} property="article:tag" content={tag} />
        ))}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={imageUrl} />

        {/* Schema.org Article Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "image": imageUrl,
            "datePublished": post.date,
            "author": {
              "@type": "Organization",
              "name": post.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "SquareCount",
              "logo": {
                "@type": "ImageObject",
                "url": "https://squarecount.io/logo.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": postUrl
            },
            "keywords": post.tags.join(', ')
          })}
        </script>
      </Helmet>
      <Navbar />

      <article className="max-w-4xl mx-auto px-6 py-16">
        <Link to="/blog" className="inline-flex items-center text-orange-500 hover:text-orange-400 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        <header className="mb-12">
          <h1 className="font-heading font-black text-4xl sm:text-5xl mb-6 text-white">
            {post.title}
          </h1>

          <div className="flex items-center gap-6 text-slate-400 mb-6">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {post.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, i) => (
              <span key={i} className="text-xs bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full font-semibold">
                {tag}
              </span>
            ))}
          </div>
        </header>

        <div className="prose prose-lg prose-invert max-w-none [&_h2]:text-white [&_h3]:text-white [&_p]:text-slate-300 [&_li]:text-slate-300 [&_strong]:text-white [&_a]:text-orange-400 [&_a:hover]:text-orange-300 [&_td]:text-slate-300 [&_th]:text-white [&_blockquote]:text-slate-300 [&_blockquote]:border-orange-500">
          {post.content}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 text-center text-white">
          <h3 className="font-heading font-bold text-2xl mb-4">
            Ready to Save Money on Roof Measurements?
          </h3>
          <p className="text-lg mb-6 text-orange-100">
            Try SquareCount today. Start with 3 free measurements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-orange-500 hover:bg-slate-100 font-heading font-bold"
              onClick={() => openAuthModal('register')}
            >
              Start Your 7-Day Contractor Trial
            </Button>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-heading font-bold">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>

        {/* Related Resources — Internal Links */}
        <div className="mt-12 border-t border-slate-800 pt-8">
          <h3 className="font-heading font-bold text-xl text-white mb-4">Related Tools &amp; Pages</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Link to="/tools/pitch-calculator" className="p-3 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <p className="font-heading font-bold text-white text-sm group-hover:text-orange-400 transition-colors">Roof Pitch Calculator</p>
              <p className="text-slate-500 text-xs">Free pitch factor tool</p>
            </Link>
            <Link to="/free-alternative" className="p-3 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <p className="font-heading font-bold text-white text-sm group-hover:text-orange-400 transition-colors">Free Roof Measurement</p>
              <p className="text-slate-500 text-xs">3 free reports/month</p>
            </Link>
            <Link to="/eagleview-alternative" className="p-3 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <p className="font-heading font-bold text-white text-sm group-hover:text-orange-400 transition-colors">EagleView Alternative</p>
              <p className="text-slate-500 text-xs">90% cheaper comparison</p>
            </Link>
            <Link to="/pricing" className="p-3 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <p className="font-heading font-bold text-white text-sm group-hover:text-orange-400 transition-colors">Pricing Plans</p>
              <p className="text-slate-500 text-xs">Free, Pro &amp; Team</p>
            </Link>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}
